import httpClient from '@/shared/lib/httpClient';
import i18n from '@/shared/lib/i18n';
import {
    TableUploadResponse,
    SheetListResponse,
    TableSchemaResponse,
    TableListResponse,
    TableUpload,
    FileInfo,
    TableSchemaDetailResponse,
    UpdateColumnDescriptionResponse,
    ProcessingProgress,
    TableUploadProgressEvent,
    BatchSheetConfig,
    BatchUploadResponse,
    UploadProgressResponse,
    CsvBatchUploadResponse,
} from '@/shared/types/table-upload';

interface TableUploadRequest {
    file: File;
    table_name?: string;
    sheet_name?: string;
    column_descriptions_sheet_name?: string;
    metadata_sheet_name?: string;
    project_id: string;
    onProgress: (data: TableUploadProgressEvent) => void;
}

interface CsvUploadRequest {
    data_file: File;
    metadata_file?: File;
    descriptions_file?: File;
    table_name: string;
    project_id: string;
    dataDelimiter: string;
    metadataDelimiter: string;
    descriptionsDelimiter: string;
    onProgress: (data: TableUploadProgressEvent) => void;
}

export interface ExportJob {
    id: number;
    project_id: string;
    table_name?: string;
    is_batch: boolean;
    selected_tables?: string[];
    upload_ids: number[];
    status: 'pending' | 'processing' | 'success' | 'error' | 'cancelled';
    progress_percentage: number;
    download_url?: string;
    error_message?: string;
}

/** @deprecated Use presigned flow from `@/features/upload`. */
export async function uploadExcelFile(request: TableUploadRequest): Promise<void> {
    const formData = new FormData();
    formData.append('file', request.file);
    if (request.table_name) {
        formData.append('table_name', request.table_name);
    }
    formData.append('project_id', request.project_id);

    if (request.sheet_name) {
        formData.append('sheet_name', request.sheet_name);
    }
    if (request.column_descriptions_sheet_name) {
        formData.append('column_descriptions_sheet_name', request.column_descriptions_sheet_name);
    }
    if (request.metadata_sheet_name) {
        formData.append('metadata_sheet_name', request.metadata_sheet_name);
    }

    await httpClient.uploadFile<void>(
        '/table-uploads/upload',
        formData,
        { isEventStream: true, onProgress: request.onProgress }
    );
}

/** @deprecated Use presigned flow from `@/features/upload`. */
export async function uploadCsvTrio(request: CsvUploadRequest): Promise<void> {
    const formData = new FormData();
    formData.append('data_file', request.data_file);
    formData.append('table_name', request.table_name);
    formData.append('project_id', request.project_id);
    formData.append('data_delimiter', request.dataDelimiter);
    formData.append('metadata_delimiter', request.metadataDelimiter);
    formData.append('descriptions_delimiter', request.descriptionsDelimiter);

    if (request.metadata_file) {
        formData.append('metadata_file', request.metadata_file);
    }
    if (request.descriptions_file) {
        formData.append('descriptions_file', request.descriptions_file);
    }

    await httpClient.uploadFile<void>(
        '/csv/upload-trio',
        formData,
        { isEventStream: true, onProgress: request.onProgress }
    );
}

export async function getExcelSheets(file: File): Promise<SheetListResponse> {
    const formData = new FormData();
    formData.append('file', file);
    return httpClient.uploadFile<SheetListResponse>('/table-uploads/excel/sheets', formData);
}

export async function listTables(): Promise<TableListResponse> {
    return httpClient.get<TableListResponse>('/table-uploads/tables');
}

export async function getTableSchema(tableName: string): Promise<TableSchemaResponse> {
    return httpClient.get<TableSchemaResponse>(`/table-uploads/tables/${tableName}`);
}

export async function getProjectTableUploads(projectId: string): Promise<TableUpload[]> {
    return httpClient.get<TableUpload[]>(`/projects/${projectId}/table-uploads`);
}

export async function activateTableUpload(projectId: string, uploadId: number): Promise<void> {
    return httpClient.put<void>(`/projects/${projectId}/table-uploads/${uploadId}/activate`);
}

export async function activateTableUploadWithTable(
    projectId: string,
    uploadId: number,
    tableName: string,
): Promise<void> {
    return httpClient.put<void>(`/projects/${projectId}/table-uploads/${uploadId}/activate`, {
        table_name: tableName,
    });
}

export async function deleteTableUpload(projectId: string, uploadId: number): Promise<void> {
    return httpClient.delete<void>(`/projects/${projectId}/table-uploads/${uploadId}`);
}

export function convertToFileInfo(upload: TableUpload): FileInfo {
    return {
        id: upload.id,
        name: upload.original_filename ?? '',
        description: upload.description,
        size: 'N/A',
        uploadedAt: new Date(upload.created_at),
        isActive: upload.is_active,
        tableName: upload.table_name || undefined,
        projectId: upload.project_id,
        appendFiles: upload.append_files,
    };
}

export function formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export function validateExcelFile(file: File, maxSizeMB: number = 200): { isValid: boolean; error?: string } {
    const validTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel.sheet.binary.macroEnabled.12',
        'application/vnd.ms-excel.sheet.macroEnabled.12',
        'application/vnd.ms-excel.sheet.macroenabled.12',
        'application/vnd.apache.parquet',
    ];

    const validExtensions = ['.xls', '.xlsx', '.xlsb', '.xlsm', '.parquet'];

    const isValidType = validTypes.includes(file.type);
    const isValidExtension = validExtensions.some((ext) => file.name.toLowerCase().endsWith(ext));

    if (!isValidType && !isValidExtension) {
        return {
            isValid: false,
            error: i18n.t('excel:invalidFileType'),
        };
    }

    const maxSize = maxSizeMB * 1024 * 1024;
    if (file.size > maxSize) {
        return {
            isValid: false,
            error: i18n.t('excel:fileTooLarge', { size: maxSizeMB }),
        };
    }

    return { isValid: true };
}

export function validateTableName(tableName: string): { isValid: boolean; error?: string } {
    if (!tableName.trim()) {
        return {
            isValid: false,
            error: i18n.t('excel:tableNameRequired'),
        };
    }

    if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(tableName.trim())) {
        return {
            isValid: false,
            error: i18n.t('excel:tableNameInvalid'),
        };
    }

    return { isValid: true };
}

export async function getTableSchemaDetail(tableName: string): Promise<TableSchemaDetailResponse> {
    return httpClient.get<TableSchemaDetailResponse>(`/tables/${tableName}/schema`);
}

export async function updateColumnDescription(
    tableName: string,
    columnName: string,
    description: string,
): Promise<UpdateColumnDescriptionResponse> {
    return httpClient.put<UpdateColumnDescriptionResponse>(
        `/tables/${tableName}/columns/${columnName}/description`,
        { description },
    );
}

export async function updateTableDescription(
    tableName: string,
    description: string,
): Promise<UpdateColumnDescriptionResponse> {
    return httpClient.put<UpdateColumnDescriptionResponse>(`/tables/${tableName}/description`, {
        description,
    });
}

export async function getProcessingProgress(uploadId: number): Promise<ProcessingProgress> {
    return httpClient.get<ProcessingProgress>(`/table-uploads/progress/${uploadId}`);
}

/** @deprecated Use presigned flow from `@/features/upload`. */
export async function batchUploadExcel(request: {
    file: File;
    project_id: string;
    sheets: BatchSheetConfig[];
}): Promise<BatchUploadResponse> {
    const formData = new FormData();
    formData.append('file', request.file);
    formData.append('project_id', request.project_id);
    formData.append('sheets_config', JSON.stringify(request.sheets));
    return httpClient.uploadFile<BatchUploadResponse>('/table-uploads/batch-upload', formData);
}

/** @deprecated Use presigned flow from `@/features/upload`. */
export async function batchUploadCsv(request: {
    files: Array<{
        file: File;
        table_name: string;
        data_delimiter?: string;
    }>;
    project_id: string;
}): Promise<CsvBatchUploadResponse> {
    const formData = new FormData();
    const config = request.files.map((item) => ({
        filename: item.file.name,
        table_name: item.table_name,
        data_delimiter: item.data_delimiter ?? 'auto',
    }));

    request.files.forEach((item) => {
        formData.append('files', item.file);
    });
    formData.append('project_id', request.project_id);
    formData.append('files_config', JSON.stringify(config));

    return httpClient.uploadFile<CsvBatchUploadResponse>('/csv/batch-upload', formData);
}

export async function getUploadProgress(uploadId: number): Promise<UploadProgressResponse> {
    return httpClient.get<UploadProgressResponse>(`/table-uploads/${uploadId}/progress`);
}

export interface ActiveUploadItem {
    upload_id: number;
    table_name: string;
    sheet_name: string;
    original_filename: string;
    created_at: string | null;
}

export async function getActiveUploads(projectId: string): Promise<{ uploads: ActiveUploadItem[] }> {
    return httpClient.get<{ uploads: ActiveUploadItem[] }>(
        `/projects/${projectId}/table-uploads/active`,
    );
}

export async function cancelUpload(projectId: string, uploadId: number): Promise<void> {
    return httpClient.post<void>(`/projects/${projectId}/table-uploads/${uploadId}/cancel`, {});
}

export interface TableFileHistoryItem {
    upload_id: number;
    original_filename: string;
    sheet_name: string | null;
    is_append: boolean;
    row_count: number | null;
    created_at: string | null;
}

export async function getTableFileHistory(
    projectId: string,
    tableName: string,
): Promise<{ files: TableFileHistoryItem[] }> {
    return httpClient.get<{ files: TableFileHistoryItem[] }>(
        `/projects/${projectId}/table-uploads/history/${tableName}`,
    );
}

export async function startTableExport(
    projectId: string,
    tableName: string,
    uploadId: number,
): Promise<ExportJob> {
    return httpClient.post<ExportJob>(
        `/projects/${projectId}/tables/${tableName}/export?upload_id=${uploadId}`,
    );
}

export async function startBatchTableExport(projectId: string, uploadIds: number[]): Promise<ExportJob> {
    return httpClient.post<ExportJob>(`/projects/${projectId}/tables/export-batch`, {
        upload_ids: uploadIds,
    });
}

export async function listActiveExports(projectId: string): Promise<ExportJob[]> {
    return httpClient.get<ExportJob[]>(`/projects/${projectId}/exports`);
}

export async function getExportStatus(jobId: number): Promise<ExportJob> {
    return httpClient.get<ExportJob>(`/exports/${jobId}`);
}

export async function dismissExportJob(jobId: number): Promise<void> {
    return httpClient.post<void>(`/exports/${jobId}/dismiss`, {});
}

export async function cancelExportJob(jobId: number): Promise<void> {
    return httpClient.delete<void>(`/exports/${jobId}`);
}

const TableUploadService = {
    uploadExcelFile,
    uploadCsvTrio,
    getExcelSheets,
    listTables,
    getTableSchema,
    getProjectTableUploads,
    activateTableUpload,
    activateTableUploadWithTable,
    deleteTableUpload,
    convertToFileInfo,
    formatBytes,
    validateExcelFile,
    validateTableName,
    getTableSchemaDetail,
    updateColumnDescription,
    updateTableDescription,
    getProcessingProgress,
    batchUploadExcel,
    batchUploadCsv,
    getUploadProgress,
    getActiveUploads,
    cancelUpload,
    startTableExport,
    startBatchTableExport,
    listActiveExports,
    getExportStatus,
    dismissExportJob,
    cancelExportJob,
};

export default TableUploadService;
